import { t } from 'i18next';

import AdvancedFilters from 'components/desktop/BaseIngredient/Search/Components/AdvancedFilters/AdvancedFilters';
import SearchResultList from 'components/desktop/BaseIngredient/Search/Components/SearchResultList/SearchResultList';
import { useBaseIngredientSearchUrl } from 'components/desktop/BaseIngredient/Search/useBaseIngredientSearchUrl';
import useSearchState from 'components/desktop/BaseIngredient/Search/useSearchState';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import DropdownPageSize from 'components/desktop/_general/DropdownPageSize/DropdownPageSize';
import DropdownSortOrder from 'components/desktop/_general/DropdownSortOrder/DropdownSortOrder';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import { useCollapsibleHook } from 'hooks/useCollapsible';
import { usePermissions } from 'hooks/usePermissions';
import { useReaction } from 'hooks/useReaction';
import { ISelectItem } from 'interfaces/ISelectItem';
import * as BaseIngredientSearchResponse from 'types/BaseIngredient/Search/BaseIngredientSearchResponseDto';
import * as Search from 'types/BaseIngredient/Search/Search';

interface IProps {
	archive: boolean;
}

const maxNumberOfButtons = 10;

const BaseIngredientSearchComponent = ({ archive }: IProps) => {
	const { isBaseIngredientEditor } = usePermissions();
	const { search, searchHandlers } = useBaseIngredientSearchUrl(archive);
	const { state, handlers } = useSearchState({
		searchTerm: search.searchTerm,
		showAdvancedSearch: Search.hasAdvancedSearchParameters(search),
	});
	const [baseIngredientResponse, isLoaded] = useReaction<
		BaseIngredientSearchResponse.Type,
		Search.Type
	>(BaseIngredientSearchResponse.create(), BaseIngredientSearchResponse.getFromApi, search);

	const getSuggestedBaseIngredients = async (): Promise<ISelectItem[]> => {
		return BaseIngredientSearchResponse.getFromApiSW(state.searchTerm, search.showOnlyDeleted);
	};

	const collapse = useCollapsibleHook(Search.hasAdvancedSearchParameters(search));

	return (
		<main className="container">
			<div className="d-flex justify-content-between">
				<RenderIf condition={!search.showOnlyDeleted}>
					<h1>{t('baseIngredient:SEARCH_BASEINGREDIENT')}</h1>
				</RenderIf>
				<RenderIf condition={search.showOnlyDeleted}>
					<h1>{t('baseIngredient:BASEINGREDIENT_ARCHIVE')}</h1>
				</RenderIf>
				<RenderIf condition={isBaseIngredientEditor}>
					<BtnNew
						link={'/baseIngredient/new'}
						name={t('baseIngredient:CREATE_BASEINGREDIENT')}
					/>
				</RenderIf>
			</div>
			<br />
			<div className="row">
				<div className="col-lg-7">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedBaseIngredients}
							handleSearch={(input) =>
								searchHandlers.changeSearchTerm(input ? input : state.searchTerm)
							}
							searchTerm={state.searchTerm}
							setSearchTerm={handlers.setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
				<div className="col-lg-2">
					<button
						className="btn btn-primary"
						type="button"
						onClick={collapse.handleToggle}
					>
						{t('_general:ADVANCED_FILTER')}
					</button>
				</div>
				<div className="col-lg-3 text-end">
					<div className="d-flex flex-row justify-content-end">
						<DropdownPageSize
							className="me-2"
							listFunction={searchHandlers.changePageSize}
							currentValue={String(search.pageSize)}
						/>
						<DropdownSortOrder
							type={'baseIngredient'}
							listFunction={searchHandlers.changeSortOrder}
							currentValue={search.sortOrder}
						/>
					</div>
				</div>
			</div>
			<div className={collapse.className} style={collapse.style} ref={collapse.ref}>
				<div className="card card-body mb-3">
					<AdvancedFilters
						archive={archive}
						disabled={baseIngredientResponse.totalCount === 0}
						handleChangeAllergens={searchHandlers.changeAllergens}
						handleChangeCategories={searchHandlers.changeCategories}
						handleChangeCharacteristics={searchHandlers.changeCharacteristics}
						handleChangeCo2Labels={searchHandlers.changeCo2Labels}
						handleChangeNutriScoreLabels={searchHandlers.changeNutriScoreLabels}
						handleChangeSeasons={searchHandlers.changeSeasons}
						handleChangeSliders={searchHandlers.changeSlider}
						handleChangeTags={searchHandlers.changeTags}
						handleReset={searchHandlers.reset}
						search={search}
						searchResponse={baseIngredientResponse}
						showAdvancedSearch={collapse.isOpen}
					/>
				</div>
			</div>
			<div className="mb-3">
				<RenderIf condition={isLoaded}>
					<SearchResultList
						archive={search.showOnlyDeleted}
						baseIngredientList={baseIngredientResponse.data}
						handleArchiveBaseIngredient={searchHandlers.archive}
						handleRestoreBaseIngredient={searchHandlers.restore}
					/>
				</RenderIf>
				<RenderIf condition={!isLoaded}>
					<LoadingAnimation isLoading={!isLoaded} />
				</RenderIf>
			</div>
			<Pagination
				countOfAllListItems={baseIngredientResponse.totalCount}
				pageIndex={search.pageIndex}
				setPageIndex={searchHandlers.changePageIndex}
				listItemAmount={search.pageSize}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default BaseIngredientSearchComponent;
